import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Save, CheckCircle, XCircle, 
  Loader2, Download, Tag, Sparkles, SplitSquareVertical, Settings2,
  FileText, Table2, AlertCircle, Eye
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All available fields for franchise leads
const TARGET_FIELDS = [
  { value: 'skip', label: 'Skip (do not import)', group: 'none' },
  { value: 'first_name', label: 'First Name *', group: 'basic', required: true },
  { value: 'last_name', label: 'Last Name', group: 'basic' },
  { value: 'full_name', label: 'Full Name (will split)', group: 'basic', special: true },
  { value: 'email', label: 'Email', group: 'contact' },
  { value: 'phone', label: 'Phone', group: 'contact' },
  { value: 'linkedin_url', label: 'LinkedIn URL', group: 'contact' },
  { value: 'address', label: 'Address', group: 'location' },
  { value: 'city', label: 'City', group: 'location' },
  { value: 'state', label: 'State', group: 'location' },
  { value: 'zip_code', label: 'ZIP Code', group: 'location' },
  { value: 'current_employer', label: 'Current Employer', group: 'work' },
  { value: 'years_experience', label: 'Years of Experience', group: 'work' },
  { value: 'franchise_interest', label: 'Franchise Interest', group: 'franchise' },
  { value: 'investment_budget', label: 'Investment Budget', group: 'franchise' },
  { value: 'territory_preference', label: 'Territory Preference', group: 'franchise' },
  { value: 'lead_status', label: 'Lead Status', group: 'status' },
  { value: 'interest_in', label: 'Interest In (semicolon-separated tags)', group: 'status' },
  { value: 'lists', label: 'Lists (semicolon-separated names)', group: 'status' },
  { value: 'lead_owner', label: 'Lead Owner', group: 'tracking' },
  { value: 'lead_source', label: 'Lead Source', group: 'tracking' },
  { value: 'notes', label: 'Notes', group: 'other' },
  { value: 'status', label: 'Pipeline Status', group: 'other' },
  { value: 'dnc', label: 'Do Not Call (Yes/No)', group: 'other' },
  { value: 'dne', label: 'Do Not Email (Yes/No)', group: 'other' },
];

// Fields that can have static values set - Updated to remove unwanted fields and add batch_name
const STATIC_CAPABLE_FIELDS = [
  { value: 'lead_status', label: 'Lead Status', options: ['new', 'interested', 'not_interested'] },
  { value: 'interest_in', label: 'Interest In', options: ['Franchise', 'JobTalk', 'NotInterested', 'Leadership'], multi: true },
  { value: 'status', label: 'Pipeline Status', options: ['new', 'contacted', 'qualified', 'negotiating', 'won', 'lost'] },
  { value: 'franchise_interest', label: 'Franchise Interest', options: ['high', 'medium', 'low'] },
  { value: 'lead_owner', label: 'Lead Owner', freeText: true },
  { value: 'lead_source', label: 'Lead Source', freeText: true },
];

// Step indicator
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
            index + 1 === currentStep ? 'bg-indigo-600 text-white' : 
            index + 1 < currentStep ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-600'
          }`}>
            {index + 1 < currentStep ? <CheckCircle className="h-4 w-4" /> : index + 1}
          </div>
          <span className="text-xs mt-1 text-slate-500">{step}</span>
        </div>
        {index < steps.length - 1 && <ArrowRight className="h-4 w-4 text-slate-400 mb-4" />}
      </React.Fragment>
    ))}
  </div>
);

export default function FranchiseImportExportModal({ open, onOpenChange, onImportComplete, mode = 'import' }) {
  const [activeTab, setActiveTab] = useState(mode);
  const [step, setStep] = useState(1);
  const [batchName, setBatchName] = useState('');
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('csv');
  const [headers, setHeaders] = useState([]);
  const [data, setData] = useState([]);
  const [mappings, setMappings] = useState({});
  const [staticValues, setStaticValues] = useState({});
  const [enableNameSplit, setEnableNameSplit] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [aiMapping, setAiMapping] = useState(false);
  const fileInputRef = useRef(null);

  const steps = ['Upload', 'Map & Configure', 'Preview', 'Complete'];

  const resetState = useCallback(() => {
    setStep(1);
    setBatchName('');
    setFile(null);
    setFileType('csv');
    setHeaders([]);
    setData([]);
    setMappings({});
    setStaticValues({});
    setEnableNameSplit(false);
    setImportResults(null);
    setAiMapping(false);
  }, []);

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return { headers: [], data: [] };

    const parseRow = (row) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const parsedHeaders = parseRow(lines[0]);
    const parsedData = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const row = {};
      const values = parseRow(lines[i]);
      parsedHeaders.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      parsedData.push(row);
    }

    return { headers: parsedHeaders, data: parsedData };
  };

  // Parse Excel file
  const parseExcel = (buffer) => {
    try {
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      
      if (jsonData.length < 2) return { headers: [], data: [] };
      
      const parsedHeaders = jsonData[0].map(h => String(h || '').trim());
      const parsedData = [];
      
      for (let i = 1; i < jsonData.length; i++) {
        const row = {};
        parsedHeaders.forEach((header, index) => {
          row[header] = jsonData[i]?.[index] != null ? String(jsonData[i][index]).trim() : '';
        });
        // Only add rows that have at least some data
        if (Object.values(row).some(v => v)) {
          parsedData.push(row);
        }
      }
      
      return { headers: parsedHeaders.filter(h => h), data: parsedData };
    } catch (error) {
      console.error('Excel parse error:', error);
      return { headers: [], data: [] };
    }
  };

  // AI-powered field mapping
  const handleAiMapping = async () => {
    if (headers.length === 0) return;
    
    setAiMapping(true);
    try {
      const response = await axios.post(`${API}/franchise/leads/ai-map-fields`, {
        headers,
        sample_data: data.slice(0, 3)
      });
      
      const aiMappings = response.data.mappings || {};
      setMappings(aiMappings);
      
      // Check if full_name was detected
      if (Object.values(aiMappings).includes('full_name')) {
        setEnableNameSplit(true);
      }
      
      toast.success(response.data.ai_powered ? 'AI mapping complete!' : 'Smart mapping applied');
    } catch (error) {
      toast.error('Mapping suggestion failed');
    } finally {
      setAiMapping(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const fileName = uploadedFile.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setFileType(isCSV ? 'csv' : 'xlsx');

    const reader = new FileReader();
    reader.onload = async (event) => {
      let parsed;
      
      if (isCSV) {
        parsed = parseCSV(event.target.result);
      } else {
        parsed = parseExcel(event.target.result);
      }
      
      if (parsed.headers.length === 0) {
        toast.error('Could not parse file. Please check the format.');
        return;
      }

      setFile(uploadedFile);
      setHeaders(parsed.headers);
      setData(parsed.data);

      // Generate default batch name
      const date = new Date();
      const defaultBatchName = `Import_${date.toISOString().split('T')[0]}_${date.getHours()}${String(date.getMinutes()).padStart(2, '0')}`;
      setBatchName(defaultBatchName);
      
      // Initial basic mapping
      const basicMappings = {};
      parsed.headers.forEach(header => {
        basicMappings[header] = 'skip';
      });
      setMappings(basicMappings);
      
      setStep(2);
      
      // Auto-trigger AI mapping
      setTimeout(() => {
        handleAiMappingWithData(parsed.headers, parsed.data);
      }, 500);
    };

    if (isCSV) {
      reader.readAsText(uploadedFile);
    } else {
      reader.readAsArrayBuffer(uploadedFile);
    }
  };

  const handleAiMappingWithData = async (hdrs, dt) => {
    setAiMapping(true);
    try {
      const response = await axios.post(`${API}/franchise/leads/ai-map-fields`, {
        headers: hdrs,
        sample_data: dt.slice(0, 3)
      });
      
      const aiMappings = response.data.mappings || {};
      setMappings(aiMappings);
      
      if (Object.values(aiMappings).includes('full_name')) {
        setEnableNameSplit(true);
      }
      
      toast.success(response.data.ai_powered ? 'AI analyzed your file!' : 'Fields auto-mapped');
    } catch {
      // Silent fail, user can manually map
    } finally {
      setAiMapping(false);
    }
  };

  const handleMappingChange = (header, targetField) => {
    setMappings(prev => ({ ...prev, [header]: targetField }));
    
    // Auto-enable name split if full_name is selected
    if (targetField === 'full_name') {
      setEnableNameSplit(true);
    }
  };

  const handleStaticValueChange = (field, value) => {
    setStaticValues(prev => {
      if (!value || value === '__none__' || (Array.isArray(value) && value.length === 0)) {
        const newVals = { ...prev };
        delete newVals[field];
        return newVals;
      }
      return { ...prev, [field]: value };
    });
  };

  const toggleStaticTag = (field, tag) => {
    const current = staticValues[field] || [];
    const arr = Array.isArray(current) ? current : [];
    if (arr.includes(tag)) {
      handleStaticValueChange(field, arr.filter(t => t !== tag));
    } else {
      handleStaticValueChange(field, [...arr, tag]);
    }
  };

  // Transform data based on mappings
  const getTransformedData = () => {
    return data.map(row => {
      const lead = {};
      Object.entries(mappings).forEach(([csvHeader, targetField]) => {
        if (targetField !== 'skip' && row[csvHeader]) {
          lead[targetField] = row[csvHeader];
        }
      });
      return lead;
    }).filter(lead => lead.first_name || lead.full_name);
  };

  const handlePreview = () => {
    if (!batchName.trim()) {
      toast.error('Please enter a batch name');
      return;
    }
    
    const hasFirstNameMapping = Object.values(mappings).includes('first_name') || 
                                (Object.values(mappings).includes('full_name') && enableNameSplit);
    if (!hasFirstNameMapping) {
      toast.error('You must map First Name or Full Name (with split enabled)');
      return;
    }
    
    setStep(3);
  };

  const handleConfirmImport = () => {
    setConfirmDialogOpen(true);
  };

  const handleImport = async () => {
    setConfirmDialogOpen(false);
    setImporting(true);
    setImportResults(null);

    try {
      const transformedData = getTransformedData();

      if (transformedData.length === 0) {
        toast.error('No valid leads to import.');
        setImporting(false);
        return;
      }

      const response = await axios.post(`${API}/franchise/leads/import-enhanced`, {
        batch_name: batchName.trim(),
        leads: transformedData,
        static_values: staticValues,
        name_split: {
          enabled: enableNameSplit,
          source_field: 'full_name'
        }
      });

      setImportResults({
        success: response.data.imported || 0,
        failed: response.data.failed || 0,
        errors: response.data.errors || [],
        batch_name: batchName,
        static_values_applied: response.data.static_values_applied || []
      });
      setStep(4);
      
      if (response.data.imported > 0) {
        onImportComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Export functionality
  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/franchise/leads/export`, {
        params: { format },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `franchise_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const transformedData = step >= 3 ? getTransformedData() : [];
  const mappedFieldsCount = Object.values(mappings).filter(v => v !== 'skip').length;
  const staticFieldsCount = Object.keys(staticValues).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
            Franchise Leads - Import / Export
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetState(); }} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="import" className="gap-2">
              <Upload className="h-4 w-4" /> Import
            </TabsTrigger>
            <TabsTrigger value="export" className="gap-2">
              <Download className="h-4 w-4" /> Export
            </TabsTrigger>
          </TabsList>

          {/* IMPORT TAB */}
          <TabsContent value="import" className="flex-1 overflow-y-auto">
            <StepIndicator currentStep={step} steps={steps} />

            {/* Step 1: Upload */}
            {step === 1 && (
              <div className="py-4">
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center hover:border-indigo-400 transition-colors cursor-pointer bg-slate-50/50"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="file-upload-zone"
                >
                  <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-700">Click to upload or drag and drop</p>
                  <div className="text-sm text-slate-500 mt-1 flex items-center justify-center gap-2">
                    <Badge variant="outline">CSV</Badge>
                    <Badge variant="outline">XLS</Badge>
                    <Badge variant="outline">XLSX</Badge>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>

                <div className="mt-6 grid grid-cols-2 gap-6">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Table2 className="h-4 w-4 text-indigo-600" />
                      <span className="font-medium text-slate-700">Available Fields</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-sm text-slate-600">
                      {TARGET_FIELDS.filter(f => f.value !== 'skip').map(f => (
                        <span key={f.value} className="flex items-center gap-1 py-0.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${f.required ? 'bg-red-400' : 'bg-indigo-400'}`}></span>
                          {f.label.replace(' *', '')} {f.required && <span className="text-red-500">*</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="h-4 w-4 text-indigo-600" />
                      <span className="font-medium text-slate-700">AI-Powered Features</span>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                        <span>Auto-detect and map columns using AI</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <SplitSquareVertical className="h-4 w-4 text-blue-500 mt-0.5" />
                        <span>Split &quot;Full Name&quot; into First &amp; Last Name</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Settings2 className="h-4 w-4 text-purple-500 mt-0.5" />
                        <span>Set static values for all imported leads</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Map & Configure */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Header row */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div>
                      <Label className="text-sm font-medium">Batch Name *</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Tag className="h-4 w-4 text-slate-400" />
                        <Input 
                          value={batchName} 
                          onChange={(e) => setBatchName(e.target.value)} 
                          placeholder="Enter batch name"
                          className="w-48"
                          data-testid="batch-name-input"
                        />
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleAiMapping}
                      disabled={aiMapping}
                      className="gap-2 mt-5"
                    >
                      {aiMapping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      AI Re-Map
                    </Button>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className="gap-1">
                        <FileText className="h-3 w-3" /> {file?.name}
                      </Badge>
                      <Badge className="bg-indigo-100 text-indigo-700">{data.length} rows</Badge>
                      <Badge className="bg-green-100 text-green-700">{mappedFieldsCount} mapped</Badge>
                    </div>
                  </div>
                </div>

                {/* Name Split Toggle */}
                {Object.values(mappings).includes('full_name') && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <SplitSquareVertical className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-slate-700">Split Full Name</p>
                        <p className="text-xs text-slate-500">Automatically separate into First and Last Name</p>
                      </div>
                    </div>
                    <Switch 
                      checked={enableNameSplit} 
                      onCheckedChange={setEnableNameSplit}
                      data-testid="name-split-toggle"
                    />
                  </div>
                )}

                {/* Mapping Table - System fields on LEFT, File columns on RIGHT */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[280px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead className="w-1/3 bg-slate-50">System Field</TableHead>
                          <TableHead className="w-1/3 bg-slate-50">File Column</TableHead>
                          <TableHead className="w-1/3 bg-slate-50">Sample Data</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {TARGET_FIELDS.filter(f => f.value !== 'skip').map(field => {
                          // Find which header maps to this field
                          const mappedHeader = Object.entries(mappings).find(([h, target]) => target === field.value)?.[0];
                          return (
                            <TableRow key={field.value} className={!mappedHeader ? 'opacity-50 bg-slate-50' : ''}>
                              <TableCell className="font-medium">
                                {field.label}
                                {field.special && <span className="text-blue-500 ml-1">(AI)</span>}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={mappedHeader || 'skip'}
                                  onValueChange={(val) => {
                                    // Clear old mapping first
                                    if (mappedHeader) {
                                      setMappings(prev => ({ ...prev, [mappedHeader]: 'skip' }));
                                    }
                                    // Set new mapping
                                    if (val && val !== 'skip') {
                                      setMappings(prev => ({ ...prev, [val]: field.value }));
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-full" data-testid={`mapping-${field.value}`}>
                                    <SelectValue placeholder="Select column..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="skip">— Not mapped —</SelectItem>
                                    {headers.map(header => (
                                      <SelectItem 
                                        key={header} 
                                        value={header}
                                        disabled={mappings[header] && mappings[header] !== 'skip' && mappings[header] !== field.value}
                                      >
                                        {header}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="text-slate-500 truncate max-w-[200px]">
                                {mappedHeader && data[0]?.[mappedHeader] ? data[0][mappedHeader] : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Static Values Section */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings2 className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-slate-700">Static Values</span>
                    <span className="text-xs text-slate-500">(Apply to ALL imported leads)</span>
                    {staticFieldsCount > 0 && (
                      <Badge className="bg-purple-100 text-purple-700 ml-2">{staticFieldsCount} set</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {STATIC_CAPABLE_FIELDS.map(field => (
                      <div key={field.value} className="space-y-1">
                        <Label className="text-xs">{field.label}</Label>
                        {field.multi ? (
                          <div className="flex flex-wrap gap-1">
                            {field.options.map(opt => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => toggleStaticTag(field.value, opt)}
                                className={`px-2 py-1 rounded text-xs border transition-all ${
                                  (staticValues[field.value] || []).includes(opt)
                                    ? 'bg-purple-100 text-purple-700 border-purple-300'
                                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`static-${field.value}-${opt}`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        ) : field.freeText ? (
                          <Input 
                            value={staticValues[field.value] || ''} 
                            onChange={(e) => handleStaticValueChange(field.value, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                            className="h-8 text-sm"
                            data-testid={`static-${field.value}`}
                          />
                        ) : (
                          <Select 
                            value={staticValues[field.value] || ''} 
                            onValueChange={(val) => handleStaticValueChange(field.value, val)}
                          >
                            <SelectTrigger className="h-8 text-sm" data-testid={`static-${field.value}`}>
                              <SelectValue placeholder="No static value" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">No static value</SelectItem>
                              {field.options.map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  <Button onClick={handlePreview} data-testid="preview-btn">
                    <Eye className="h-4 w-4 mr-2" /> Preview Data
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Step 3: Preview */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-700">Preview Import</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline">Batch: {batchName}</Badge>
                      <Badge className="bg-indigo-100 text-indigo-700">{transformedData.length} leads</Badge>
                      {enableNameSplit && <Badge className="bg-blue-100 text-blue-700">Name Split ON</Badge>}
                      {staticFieldsCount > 0 && <Badge className="bg-purple-100 text-purple-700">{staticFieldsCount} static values</Badge>}
                    </div>
                  </div>
                </div>

                {/* Static values summary */}
                {staticFieldsCount > 0 && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-sm font-medium text-purple-700 mb-2">Static values to apply:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(staticValues).map(([field, value]) => (
                        <Badge key={field} variant="outline" className="text-purple-700 border-purple-300">
                          {field}: {Array.isArray(value) ? value.join(', ') : value}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead className="bg-slate-50 w-12">#</TableHead>
                          <TableHead className="bg-slate-50">First Name</TableHead>
                          <TableHead className="bg-slate-50">Last Name</TableHead>
                          <TableHead className="bg-slate-50">Email</TableHead>
                          <TableHead className="bg-slate-50">Phone</TableHead>
                          <TableHead className="bg-slate-50">City</TableHead>
                          <TableHead className="bg-slate-50">Lead Status</TableHead>
                          <TableHead className="bg-slate-50">Interest In</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transformedData.slice(0, 50).map((lead, index) => {
                          // Preview name split
                          let firstName = lead.first_name || '';
                          let lastName = lead.last_name || '';
                          if (enableNameSplit && lead.full_name && !firstName) {
                            const parts = lead.full_name.trim().split(' ');
                            firstName = parts[0] || '';
                            lastName = parts.slice(1).join(' ') || '';
                          }
                          
                          return (
                            <TableRow key={index}>
                              <TableCell className="text-slate-400">{index + 1}</TableCell>
                              <TableCell className="font-medium">{firstName || '—'}</TableCell>
                              <TableCell>{lastName || '—'}</TableCell>
                              <TableCell className="text-slate-600">{lead.email || '—'}</TableCell>
                              <TableCell className="text-slate-600">{lead.phone || '—'}</TableCell>
                              <TableCell>{lead.city || staticValues.city || '—'}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {lead.lead_status || staticValues.lead_status || 'new'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {(lead.interest_in || staticValues.interest_in || []).length > 0 ? (
                                  <div className="flex gap-1">
                                    {(lead.interest_in || staticValues.interest_in || []).slice(0, 2).map(t => (
                                      <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                                    ))}
                                  </div>
                                ) : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                
                {transformedData.length > 50 && (
                  <p className="text-sm text-slate-500 text-center">
                    Showing first 50 of {transformedData.length} leads
                  </p>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Mapping
                  </Button>
                  <Button onClick={handleConfirmImport} disabled={importing} className="bg-green-600 hover:bg-green-700" data-testid="import-btn">
                    {importing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
                    ) : (
                      <><Save className="h-4 w-4 mr-2" /> Import {transformedData.length} Leads</>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Step 4: Results */}
            {step === 4 && importResults && (
              <div className="py-8 text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  importResults.success > 0 ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {importResults.success > 0 ? (
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  ) : (
                    <XCircle className="h-8 w-8 text-red-600" />
                  )}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Import {importResults.success > 0 ? 'Complete' : 'Failed'}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Batch: <span className="font-medium">{importResults.batch_name}</span>
                </p>
                <div className="flex items-center justify-center gap-6 text-lg">
                  <div>
                    <span className="font-bold text-green-600">{importResults.success}</span>
                    <span className="text-slate-500 ml-1">imported</span>
                  </div>
                  {importResults.failed > 0 && (
                    <div>
                      <span className="font-bold text-red-600">{importResults.failed}</span>
                      <span className="text-slate-500 ml-1">failed</span>
                    </div>
                  )}
                </div>
                
                {importResults.static_values_applied?.length > 0 && (
                  <p className="text-sm text-purple-600 mt-3">
                    Static values applied: {importResults.static_values_applied.join(', ')}
                  </p>
                )}
                
                {importResults.errors?.length > 0 && (
                  <div className="mt-4 text-left max-h-32 overflow-y-auto bg-red-50 rounded p-3 text-sm text-red-700">
                    {importResults.errors.slice(0, 5).map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                    {importResults.errors.length > 5 && (
                      <p className="text-red-500 mt-1">...and {importResults.errors.length - 5} more errors</p>
                    )}
                  </div>
                )}
                
                <DialogFooter className="mt-6 justify-center">
                  <Button onClick={handleClose} data-testid="done-btn">Done</Button>
                </DialogFooter>
              </div>
            )}
          </TabsContent>

          {/* EXPORT TAB */}
          <TabsContent value="export" className="flex-1">
            <div className="py-6 space-y-6">
              <div className="text-center">
                <Download className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900">Export Franchise Leads</h3>
                <p className="text-sm text-slate-500 mt-1">Download all leads with complete data</p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-700 mb-3">Exported fields include:</p>
                <div className="grid grid-cols-3 gap-2 text-sm text-slate-600">
                  {TARGET_FIELDS.filter(f => f.value !== 'skip' && f.value !== 'full_name').map(f => (
                    <span key={f.value} className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {f.label.replace(' *', '')}
                    </span>
                  ))}
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    Import Batch Name
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    Created At
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    Updated At
                  </span>
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <Button 
                  onClick={() => handleExport('csv')} 
                  disabled={exporting}
                  className="gap-2"
                  data-testid="export-csv-btn"
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Export as CSV
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Confirmation Dialog */}
        <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Import</AlertDialogTitle>
              <AlertDialogDescription>
                You are about to import <strong>{transformedData.length} leads</strong> with batch name <strong>&quot;{batchName}&quot;</strong>.
                {enableNameSplit && (
                  <><br /><br /><span className="text-blue-600">Name splitting is enabled.</span></>
                )}
                {staticFieldsCount > 0 && (
                  <><br /><span className="text-purple-600">{staticFieldsCount} static value(s) will be applied to all leads.</span></>
                )}
                <br /><br />
                Are you sure you want to proceed?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleImport} className="bg-green-600 hover:bg-green-700">
                Yes, Import Leads
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
